/**
 * CreditsModal — Renders <CreditsPanel /> as a full-screen modal.
 * Mounted globally in App.tsx via <CreditsModalHost />.
 */
import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { CreditsPanel } from './CreditsPanel';
import { useCredits } from '../../contexts/CreditsContext';

export const CreditsModalHost: React.FC = () => {
  const { isPanelOpen, closeCreditsPanel, panelReturnTo } = useCredits();

  useEffect(() => {
    if (!isPanelOpen) return;
    // Lock scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCreditsPanel();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [isPanelOpen, closeCreditsPanel]);

  if (!isPanelOpen) return null;

  return (
    <div
      data-testid="credits-modal-host"
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center overflow-y-auto"
      onClick={closeCreditsPanel}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-950/85 backdrop-blur-md" />

      {/* Panel container */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl mx-auto px-4 py-6 sm:py-10"
      >
        <button
          type="button"
          onClick={closeCreditsPanel}
          data-testid="credits-modal-close"
          className="absolute top-8 right-6 h-9 w-9 rounded-full bg-slate-900/70 border border-slate-800 hover:border-cyan-400/50 text-slate-300 hover:text-cyan-300 flex items-center justify-center transition-colors z-10"
          aria-label="Cerrar"
        >
          <X size={16} />
        </button>
        <CreditsPanel variant="modal" returnTo={panelReturnTo} />
      </div>
    </div>
  );
};

export default CreditsModalHost;
